import {useEffect, useSyncExternalStore} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import {
  acquireRelay,
  releaseRelay,
  refreshRelaySnapshot,
  subscribeRelayRegistry,
  isRelayActive,
  getRelayStatus,
  getRelayError,
} from '@github-ui/ahp-relay/relay-registry'
import type {RelayStatus} from '@github-ui/ahp-relay/relay-connection-runtime'
import {RelaySessionEvents} from '../ahp/relay-session-events'
import {mcFetcher} from '../utils/mc-fetcher'

export interface UseAhpRelayOptions {
  taskId: string | undefined
  environmentId: string | undefined
  /** Mission Control session id the relay observes. */
  sessionId: string | undefined
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: string
  /** When false the relay is inert (no connection, no registry entry). */
  enabled: boolean
}

export interface UseAhpRelayResult {
  active: boolean
  status: RelayStatus | 'idle'
  error?: string
}

/**
 * Acquires a shared AHP relay for a task (ref-counted via the registry) and
 * reports its live status. The relay populates the session-events query cache;
 * this hook only manages lifecycle + status, it does not return events.
 */
export function useAhpRelay({
  taskId,
  environmentId,
  sessionId,
  channel,
  enabled,
}: UseAhpRelayOptions): UseAhpRelayResult {
  const queryClient = useQueryClient()
  const shouldConnect = enabled && !!taskId && !!environmentId && !!sessionId

  useEffect(() => {
    if (!shouldConnect || !taskId || !environmentId || !sessionId) return
    const wasActiveBefore = isRelayActive(taskId)
    acquireRelay({
      taskId,
      environmentId,
      sessionId,
      ...(channel ? {channel} : {}),
      fetcher: mcFetcher,
      createSink: id => new RelaySessionEvents(id, queryClient),
    })
    // If we just re-attached to a relay that was already running for another
    // consumer (e.g. the preconnect ref from useCreateEnvironmentTask), force
    // a snapshot refresh. The other consumer's sink may have been torn down
    // mid-stream, leaving the events cache stale; pulling a fresh snapshot
    // from the daemon guarantees this consumer renders the current state.
    if (wasActiveBefore) {
      refreshRelaySnapshot(taskId)
    }
    return () => releaseRelay(taskId)
  }, [shouldConnect, taskId, environmentId, sessionId, channel, queryClient])

  const active = useSyncExternalStore(
    subscribeRelayRegistry,
    () => isRelayActive(taskId),
    () => false,
  )
  const status = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayStatus(taskId),
    () => 'idle' as const,
  )
  const error = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayError(taskId),
    () => undefined,
  )

  return {active, status, error}
}
